<template>
    <div class="w-full">
        <div v-if="!loading && list && total > 0" v-for="(tr, key) in list" :key="tr.id"
            @click='detail_page(tr.id)' 
            class="w-fill flex flex-column surface-0 relative overflow-hidden cursor-pointer
                border-round-xl xl:border-round-2xl
                p-3 xl:p-4
                mb-3 xl:mb-4">
            <div class="w-full flex flex-row
                mb-3 xl:mb-4" >
                <div v-if="tr.sender && tr.merchant_type=='get'" class="flex flex flex-column flex-1 overflow-hidden">
                    <span v-if="tr.sender.fullname" class="flex text-500 font-medium
                        mb-2 xl:mb-3
                        text-xs xl:text-sm">
                        <span>{{ lang('sender') }}: {{ tr.sender.fullname }}</span>
                    </span>
                    <span v-if="tr.sender.amount && tr.sender.amount && tr.sender.currency_code" 
                        class="flex font-medium text-800 white-space-nowrap
                        mb-2 xl:mb-3
                        text-xl xl:text-3xl">{{ get_format_amount(tr.sender.amount, tr.sender.currency_code) }}
                    </span>
                    <div v-if="tr.sender.bank_code" class="flex flex-row align-items-center justify-content-left gap-2">
                        <Avatar :image="getBankImage(tr.sender.bank_code)" class="flex p-avatar-sm" shape="circle" size="small"/>
                        <span class="text-500 flex white-space-nowrap vertical-align-middle font-medium text-xs xl:text-sm">
                            {{ getBankTitle(tr.sender.bank_code) }}
                        </span>
                    </div>
                </div>
                <div v-if="tr.recipient" class="flex flex flex-column flex-1">
                    <span v-if="tr.recipient.fields && tr.recipient.fields.fullname" class="flex text-500 font-medium
                        mb-2 xl:mb-3
                        text-xs xl:text-sm">
                        <span>{{ lang('recipient') }}: {{ tr.recipient.fields.fullname }}</span>
                    </span>
                    <span v-else class="flex text-500 font-medium
                        mb-2 xl:mb-3
                        text-xs xl:text-sm">
                        <span class="hidden mr-1 xl:inline lg:inline md:inline"></span> 
                        <span>{{ lang('recipient') }}</span>
                    </span>
                    <span v-if="tr.recipient.amount && tr.recipient.amount && tr.recipient.currency_code" class="flex font-medium text-800 white-space-nowrap
                        mb-2 xl:mb-3
                        text-xl xl:text-3xl">{{ get_format_amount(tr.recipient.amount, tr.recipient.currency_code) }}</span>
                    <div v-if="tr.recipient.bank_code" class="flex flex-row align-items-center justify-content-left gap-2">
                        <Avatar :image="getBankImage(tr.recipient.bank_code)" class="flex p-avatar-sm" shape="circle" size="small"/>
                        <span class="text-500 flex white-space-nowrap vertical-align-middle font-medium text-xs xl:text-sm">
                            {{ getBankTitle(tr.recipient.bank_code) }}
                        </span>
                    </div>
                </div>
                <div v-if="tr.status" class="flex flex flex-column gap-1 align-items-end">
                    <div v-if="tr.status" class="flex flex-column flex-initial xl:pr-3">
                        <div :class="'status-'+tr.status" class="inline-block font-medium border-2 surface-border text-white font-medium align-items-center
                            border-round-md xl:border-round-lg
                            p-1 xl:px-2">
                            <span class="flex text-xs xl:text-sm">{{getStatusLabel(tr.status)}}</span>
                        </div>
                    </div>
                    <div v-if="tr.merchant_type && tr.status=='completed'" class="flex align-items-center gap-1">
                        <i class="pi pi-clock text-500 text-xs font-medium"/>
                        <span class="flex py-1 text-500 text-xs font-medium">
                            {{ getTimer(tr.merchant_type, tr) }}
                        </span>
                    </div>
                </div>
            </div>
            <div class="flex flex-row gap-2">
                <div v-if="tr.merchant_type" class="flex flex-initial align-items-center">
                    <div :class="getMerchantClass(tr.merchant_type)" class="flex flex-row font-medium border-none font-medium align-items-center gap-1
                        border-round-md xl:border-round-lg
                        p-1 xl:px-2">
                        <i v-if="tr.merchant_type === 'get'" class="pi pi-arrow-down text-xs font-medium"/>
                        <i v-if="tr.merchant_type === 'send'" class="pi pi-arrow-up text-xs font-medium"/>
                        <i v-if="tr.merchant_type === 'both'" class="pi pi-sync text-xs font-medium"/>
                        <span class="flex text-xs xl:text-sm">{{getMerchantType(tr.merchant_type)}}</span>
                    </div>
                </div>
                <div v-if="tr.dates && tr.dates.created && tr.status" class="flex flex-1 align-items-center gap-1">
                    <i class="pi pi-calendar text-500 text-xs font-medium"/>
                    <span class="inline-block text-500 font-medium border-2 border-transparent
                        text-xs xl:text-sm
                        py-1">
                        <span v-if="tr.status=='created' && tr.progress && tr.progress>0">{{ this.lang('timetopay') }} {{ tr.counter }}</span>
                        <span v-else>{{ formatDate(tr.dates.created) }}</span>
                    </span>
                </div>
                <div class="flex align-items-center gap-1">
                    <span v-if="tr.id" class="flex flex-row text-500 font-medium py-1">
                        <span class="flex text-xs xl:text-sm">...{{get_short_id(tr.id)}}</span>
                    </span>
                </div>
            </div>
            <div v-if="tr.status && tr.status=='created'" class="absolute w-full bottom-0 left-0 right-0" style="height:2px">
                <div v-if="tr.progress && tr.progress>0" class="absolute bg-primary bottom-0 left-0 right-0" style="height:2px" :style="'width:'+tr.progress+'%'"></div>
            </div>
        </div>
        <div v-if="(!list && total==null) || loading" class="w-full">
            <Skeleton v-for="i in count" :key="i" height="8rem" class="mb-3 border-round-xl"/>
        </div>
        <div v-if="!loading && total==0" class="w-full">
            <span class="block text-800 mb-3">{{ lang('zero_transfers') }}</span>
        </div>
    </div>
</template>
  
<script>
import Connect from '@/assets/js/Connect'
import Helper from '@/assets/js/Helper'
import { BankList } from '@/assets/js/classes/PaymentSystem.js'
import { CurrencyList} from '@/assets/js/classes/Currency.js'

export default {
    props: {
        list: {
            type: Array,
            default: () => []
        },
        count: {
            type: Number,
            default: 10
        },
        loading: {
            type: Boolean,
            default: false
        },
        total: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            header: {
                en: {title: "My transfers"} ,
                ru: {title: "Мои переводы"} ,
            }, 
            lazyParams: {},
            skip: 0,
            transfers: null,
            banks: [],
            currencies: []
        }
    },
    methods: {
        getTimer(type, tr){
            if(!type || (type!='get' && type!='send' && type!='both')) return ''
            if(!tr || !tr.dates || !tr.dates.created) return ''
            let from, to = null
            if(type=='get') {
                from = (tr.sender.confirm_date) ? tr.sender.confirm_date : null
                to = (tr.get.merchant.confirm_date) ? tr.get.merchant.confirm_date : null
            } else if(type=='send') {
                from = (tr.get.merchant.confirm_date) ? tr.get.merchant.confirm_date : null
                to = (tr.send.merchant.confirm_date) ? tr.send.merchant.confirm_date : null
            } else if(type=='both') {
                from = (tr.sender.confirm_date) ? tr.sender.confirm_date : null
                to = (tr.send.merchant.confirm_date) ? tr.send.merchant.confirm_date : null
            }
            if(!from || !to) return ''
            return this.helper.get_timer(from, to)
        },
        lang(value){
            return this.$t(`views.personal.transferList.${value}`)
        },
        detail_page(id) {
            this.$router.push({ path: '/merchant/transfer/' + id })
        },
        getBankTitle(code){
            let bankObj = new BankList()
            bankObj.items = this.banks
            const bankCode = bankObj.GetTitle(code, this.lang_)
            return (bankCode) ? bankCode : code
        },
        getBankImage(code){
            let bankObj = new BankList()
            bankObj.items = this.banks
            const bankCode = bankObj.GetImage(code)
            return (bankCode) ? bankCode : code
        },
        formatDate(value) {
            return this.helper.format_date_short(value)
        },
        getStatusLabel (status) {
            return this.helper.get_severity_status(status, this.lang_)
        },
        getMerchantType (type) {
            return (type=='get') ? 'прием' : (type=='send') ? 'выплата' : (type=='both') ? 'круг' : '-'
        },
        getMerchantClass(type){
            return (type=='get') ? 'bg-blue-50 text-blue-600' : (type=='send') ? 'bg-orange-50 text-orange-600' : (type=='both') ? 'bg-green-50 text-green-600' : ''
        },
        get_format_amount(value, currency) {
            let digits = 2
            if(currency=='rub' || currency=='idr') digits = 0
            return this.helper.format_amount(value, currency, digits)
        },
        get_short_id(value){
            return this.helper.get_short_id(value)
        },
        async GetBanks(){
            this.banks = await BankList.GetList(this.$store).then(r=>r)
        },
        async GetCurrencies(){
            this.currencies = await CurrencyList.GetList(this.$store).then(r=>r)
        },
    },
    computed: {
        lang_() { return (this.$i18n.locale) ? this.$i18n.locale : 'ru' },
        banks_obj() { return this.$store.state.payment.banks }
    },
    watch: {
        skip(){
            window.scrollTo({ top: 0, behavior: 'smooth' })
            this.lazyParams.first = this.skip
        }
    },
    async beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
    async mounted(){
        this.GetBanks()
        this.GetCurrencies()
    }
}
</script>